package uz.mint.frontend

import kotlinx.browser.window

/**
 * Build-time site constants.
 *
 * The landing page is statically exported, so these cannot come from the backend at render
 * time — a crawler fetching the HTML must already see the real links.
 */
object SiteConfig {

    /**
     * Where purchase intent goes when online payment is unavailable, and where a customer sends
     * their receipt when a payment went wrong. Online payment is Click; this is the fallback
     * shown when it cannot be offered.
     *
     * Must match the backend's `mint.telegram.url`, which the same handle is configured under.
     */
    const val TELEGRAM_URL = "https://t.me/mintesim"

    /** The real account, confirmed by the owner 2026-08-03. */
    const val INSTAGRAM_URL = "https://www.instagram.com/mint_esim"

    /**
     * The support line, as it is printed. Given by the owner 2026-08-08.
     *
     * The design's number (+998 71 200 00 20) was invented, which is why the strip that used to
     * carry one was dropped rather than filled in. This is a real line, so it comes back.
     */
    const val PHONE = "+998 93 514 49 48"

    /**
     * The same number as a `tel:` target, **derived rather than written twice**: the printed form
     * carries spaces a dialler must not see, and two constants would drift the first time the
     * number changed — leaving a link that quietly rings the old one.
     */
    val PHONE_HREF: String get() = "tel:" + PHONE.filter { it.isDigit() || it == '+' }

    const val BRAND_NAME = "Mint.uz"

    /**
     * The site's public address, used to build absolute canonical and hreflang URLs.
     *
     * A constant on purpose, and the one place [apiOrigin]'s reasoning is inverted: that resolves
     * from `window.location` because one export is served everywhere, while this must *not*.
     * Static export renders in a headless browser on localhost, so a canonical derived from the
     * current location would ship `http://localhost:8080/` as every page's declared true address
     * — telling Google the real page lives somewhere it cannot reach.
     *
     * No trailing slash: the locale paths it is concatenated with all start with one.
     */
    const val CANONICAL_ORIGIN = "https://mintaccess.uz"

    /**
     * Origin of the backend BFF, resolved in the browser rather than at build time.
     *
     * A static export is one set of files served in every environment, so the API origin
     * cannot be compiled in. In dev the Kobweb server owns :8080 while the backend listens on
     * :8081 — a cross-origin call its CORS config already permits. Anywhere else the export is
     * expected to sit behind the same origin as the API, where a relative path is what works
     * (and avoids a second CORS preflight).
     */
    val apiOrigin: String
        get() = if (window.location.hostname in LOCAL_HOSTS) "http://localhost:8081" else ""

    private val LOCAL_HOSTS = setOf("localhost", "127.0.0.1", "[::1]")
}
